/// Redeem an iOS-issued redemption code on the web. The code is
/// minted by the iOS app's "Use my purchases on the web" Settings
/// button (Cloud Function `generateRedemptionCode`); this dialog
/// trades it for a `webUsers/{uid}.purchases.full` write via the
/// `redeemCode` Cloud Function. Single-use; expires 30 days after
/// issuance; bound to the redeeming Firebase UID.

import { useState } from 'react'
import { httpsCallable } from '../firebase/callable'
import { firebaseFunctions } from '../firebase/app'
import { logRedeemCode } from '../analytics/AnalyticsManager'

interface RedeemResponse {
  ok: true
  unlockType: string
}

const redeemCode = httpsCallable<{ code: string }, RedeemResponse>(
  firebaseFunctions,
  'redeemCode',
)

export function RedeemCodeModal(props: { onDismiss: () => void }) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await redeemCode({ code: code.trim().toUpperCase() })
      logRedeemCode({ unlockType: result.data?.unlockType ?? 'full' })
      setDone(true)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      console.warn('redeemCode failed', err)
      setError(message)
      setBusy(false)
    }
  }

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-50 overflow-y-auto bg-black/70 px-4 py-6 backdrop-blur-sm"
      onClick={busy ? undefined : props.onDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mx-auto w-full max-w-sm rounded-xl bg-slate-900 p-6 text-slate-100 shadow-2xl ring-1 ring-white/10"
      >
        <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">
          Redeem Code
        </div>
        <h2 className="mt-1 text-lg font-semibold tracking-wide">
          {done ? 'Unlocked' : 'Use your iOS purchase'}
        </h2>

        {!done && (
          <>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Got a code from the iOS app’s <em>“Use my purchases on the web”</em> button? Paste it here.
            </p>

            <form onSubmit={handleSubmit} className="mt-4 space-y-2">
              <label className="block">
                <span className="text-[11px] font-semibold text-slate-400">
                  Redemption code
                </span>
                <input
                  type="text"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={busy}
                  autoFocus
                  className="mt-1 w-full rounded-md bg-slate-800 px-3 py-2 font-mono text-base uppercase tracking-widest text-white outline-none ring-1 ring-white/10 focus:ring-emerald-500"
                  placeholder="ABCD2345"
                  maxLength={12}
                />
              </label>

              {error && (
                <div className="rounded-md bg-rose-900/50 px-3 py-2 text-xs text-rose-100 ring-1 ring-rose-500/30">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || code.trim().length === 0}
                className={
                  'mt-2 w-full rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow ' +
                  (busy ? 'opacity-60' : 'hover:bg-emerald-500')
                }
              >
                {busy ? 'Redeeming…' : 'Redeem'}
              </button>
            </form>
          </>
        )}

        {done && (
          <div className="mt-4 space-y-3">
            <div className="rounded-md bg-emerald-900/40 px-3 py-3 text-sm text-emerald-100 ring-1 ring-emerald-500/30">
              ✅ Full Game Unlock applied to your account.
            </div>
            <p className="text-xs text-slate-400">
              Locked levels on the home map should switch to playable within a second or two.
            </p>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            onClick={props.onDismiss}
            disabled={busy}
            className="text-xs text-slate-400 hover:text-white"
          >
            {done ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  )
}
